import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Merge, RefreshCw, Loader2 } from "lucide-react";
import api, { formatNumber } from "../lib/api";

function Grupo({ g, onSeparar }) {
  return (
    <tr data-testid={`maestro-${g.tipo}-${g.final}`}>
      <td><span className={"text-[10px] font-bold px-2 py-0.5 rounded-full " + (g.tipo === "laboratorio" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700")}>{g.tipo === "laboratorio" ? "Laboratorio" : "Familia"}</span></td>
      <td className="font-semibold">{g.final}</td>
      <td className="text-slate-500 text-xs">
        <div className="flex flex-wrap gap-1.5">
          {g.originales.map((o) => (
            <span key={o} className="inline-flex items-center gap-1 bg-slate-100 rounded-full pl-2 pr-1 py-0.5">
              {o}
              <button onClick={() => onSeparar(g, o)} title="Separar: este nombre no es el mismo" className="text-red-500 hover:text-red-700 font-bold px-1" data-testid={`maestro-separar-${o}`}>✕</button>
            </span>
          ))}
        </div>
      </td>
      <td className="num">{formatNumber(g.refs)}</td>
    </tr>
  );
}

export default function Maestros() {
  const [d, setD] = useState(null);
  const [todo, setTodo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [separados, setSeparados] = useState([]);
  const load = () => {
    api.get("/maestros/separados").then((r) => setSeparados(r.data.separados)).catch(() => {});
    return api.get("/maestros/fusiones").then((r) => setD(r.data.fusiones)).catch(() => setD([]));
  };
  useEffect(() => { load(); }, []);
  const separar = async (g, o) => {
    if (!window.confirm(`¿Separar «${o}» de «${g.final}»? Dejará de sumar junto a ese nombre.`)) return;
    try {
      const { data } = await api.post("/maestros/separar", { tipo: g.tipo, original: o });
      toast.success(data.restaurados > 0 ? `Separado: ${data.restaurados} referencias vuelven a «${o}»`
        : `Separado. Se aplicará en los próximos inventarios que subas (los ya subidos no guardaban el nombre original).`);
    } catch { toast.error("No se pudo separar"); }
    load();
  };
  const reunir = async (s) => {
    try {
      await api.post("/maestros/volver-a-unir", s);
      toast.success(`«${s.original}» vuelve a unificarse`);
    } catch { toast.error("No se pudo volver a unir"); }
    load();
  };
  const revisar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/maestros/normalizar");
      toast.success(`Revisado: ${data.familia} familias y ${data.laboratorio} laboratorios unificados`);
      await load();
    } finally { setBusy(false); }
  };
  if (!d) return <div className="p-8 text-slate-500 flex gap-2"><Loader2 size={16} className="animate-spin" /> Cargando…</div>;
  const fusiones = d.filter((g) => g.originales.length > 1);
  const resto = d.filter((g) => g.originales.length === 1);
  return (
    <div className="p-4 sm:p-8 max-w-6xl fade-in" data-testid="maestros-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-500">Servicios</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Familias y laboratorios</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Los nombres se unifican solos al subir: mayúsculas, espacios, «S.L.», «S.A.», comas y vacíos («nan» pasa a «SIN FAMILIA» o «SIN LABORATORIO»). Así cada laboratorio y cada familia suman en un solo total.</p>
        </div>
        <button className="btn btn-outline" onClick={revisar} disabled={busy} data-testid="maestros-revisar">{busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Revisar de nuevo</button>
      </div>
      <div className="card p-5 mb-6">
        <h3 className="font-display font-bold flex items-center gap-2 mb-1"><Merge size={16} className="text-emerald-600" /> Nombres que se han unido ({fusiones.length})</h3>
        <p className="text-xs text-slate-500 mb-3">Varios nombres distintos que eran el mismo laboratorio o familia.</p>
        {!fusiones.length ? <p className="text-sm text-slate-500" data-testid="maestros-sin-fusiones">No había nombres repetidos.</p> : (
          <div className="overflow-x-auto"><table className="dense w-full" data-testid="maestros-fusiones">
            <thead><tr><th>Tipo</th><th>Nombre final</th><th>Se escribía como</th><th className="num">Referencias</th></tr></thead>
            <tbody>{fusiones.map((g) => <Grupo key={g.tipo + g.final} g={g} onSeparar={separar} />)}</tbody>
          </table></div>
        )}
      </div>
      {separados.length > 0 && (
        <div className="card p-5 mb-6" data-testid="maestros-separados">
          <h3 className="font-display font-bold mb-1">Nombres separados ({separados.length})</h3>
          <p className="text-xs text-slate-500 mb-3">No se unifican al subir. Si te equivocaste, vuelve a unirlos.</p>
          <div className="flex flex-wrap gap-2">
            {separados.map((s) => (
              <span key={s.tipo + s.original} className="inline-flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-full pl-3 pr-1 py-1">
                <b>{s.tipo === "laboratorio" ? "Lab" : "Familia"}</b> {s.original}
                <button onClick={() => reunir(s)} className="btn btn-outline btn-sm !py-0.5" data-testid={`maestro-reunir-${s.original}`}>Volver a unir</button>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-bold">Solo cambió la forma de escribirlos ({resto.length})</h3>
            <p className="text-xs text-slate-500">Mayúsculas, espacios o la forma jurídica: no suman con otro nombre.</p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setTodo(!todo)} data-testid="maestros-ver-resto">{todo ? "Ocultar" : "Ver lista"}</button>
        </div>
        {todo && (
          <div className="overflow-x-auto mt-3"><table className="dense w-full" data-testid="maestros-resto">
            <thead><tr><th>Tipo</th><th>Nombre final</th><th>Se escribía como</th><th className="num">Referencias</th></tr></thead>
            <tbody>{resto.map((g) => <Grupo key={g.tipo + g.final} g={g} onSeparar={separar} />)}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
